import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dropdown,
  DropdownItem,
  getColor,
  Icons,
  InputArea,
  InputField,
  Tooltip,
  Typography,
} from "fds";
import "./index.scss";
import utilsDocs from "../../shared/utils/docs";
import { Member, WorkgroupCreateInfo } from "../../types/workgroup";
import UserSearchField from "../UserSearchField";
import utilsCommon from "../../shared/utils/common";
import { DocInfo, PermissionPolicy, UploadFile } from "../../types/files";
import useCheckboxGroup from "fds/src/hooks/useCheckboxGroup";
import classNames from "classnames";
import { t } from "i18next";
import i18n from "../../locales/i18n";
import useDocumentStore from "../../redux/dispatcher/useDocumentStore";
import PolicyToolTip from "../PolicyToolTip";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";
import { Trans } from "react-i18next";
import NameTag from "../NameTag";
interface CreateNewWorkgroupPageProps {
  classes?: string;
  uid?: string;
  files?: UploadFile[];
  changeFiles?: (file: UploadFile[]) => void;
  workgroupInfo?: WorkgroupCreateInfo;
  addedUserList: any[];
  disableValues: string[];
  copyFileResp?: {
    files: DocInfo[];
    workgroupId?: string;
    message?: string;
  };
  changeExtraOpenModalInfo: (type: string) => void;
  permissionList?: { targetValue: string; policy: PermissionPolicy }[];
  changeCopyFileResp?: (req: {
    files: DocInfo[];
    workgroupId?: string;
    message?: string;
  }) => void;
  changeIsNextButtonDisabled: (v: boolean) => void;
  changeWorkgroupInfo?: (info: WorkgroupCreateInfo) => void;
  changePermissionList?: (
    permission: { targetValue: string; policy: PermissionPolicy }[]
  ) => void;
  changeAddedUserList: (user: any) => void;
  changeTargetPermissionUserList?: (userIds: string[]) => void;
  modalType?: "CREATE_NEW_WORKGROUP" | "ADD_NEW_MEMBER" | "SELECT_RECEIVER";
  message?: string;
  changeMessage?: (msg: string) => void;
  clientSize?: { width: number; height: number };
}
const CreateNewWorkgroupPage: React.FC<CreateNewWorkgroupPageProps> = ({
  files,
  uid,
  changeFiles,
  classes,
  workgroupInfo,
  disableValues,
  addedUserList,
  modalType = "CREATE_NEW_WORKGROUP",
  copyFileResp,
  permissionList,
  clientSize,
  message,
  changeExtraOpenModalInfo,
  changeMessage,
  changePermissionList,
  changeIsNextButtonDisabled,
  changeWorkgroupInfo,
  changeAddedUserList,
  changeTargetPermissionUserList,
}) => {
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  const myId = loginDto?.userInfo?.userId ?? "";
  const { removeFileFromUid } = useDocumentStore();
  const checkboxGroup = useCheckboxGroup<string>();
  const [buttonClick, changeButtonClick] = useState(0);
  useEffect(() => {
    changeIsNextButtonDisabled(
      modalType === "CREATE_NEW_WORKGROUP"
        ? !workgroupInfo?.workgroupName
        : addedUserList.length === 0
    );
  }, [workgroupInfo?.workgroupName, addedUserList]);

  const handleChangeWorkgroupInfo = (key: string, value: string) => {
    workgroupInfo &&
      changeWorkgroupInfo &&
      changeWorkgroupInfo({ ...workgroupInfo, [key]: value });
  };
  const renderFileList = (files: any) => {
    return (
      <Box classes="upload-fileList">
        {files.map((file: any) => (
          <Box classes="upload-file">
            <Box>
              {utilsDocs.renderDocIcon(file.fileName ?? file.contentName, 20)}
            </Box>
            <Tooltip
              message={file.fileName ?? file.contentName}
              // maxWidth={"calc(100% - 36px)"}
              maxWidth={400}
            >
              <Typography type="body4_rg" ellipsis classes="mg ml-4">
                {file.fileName ?? file.contentName}
              </Typography>
            </Tooltip>
            {files.length > 1 && (
              <Box
                alignItems="center"
                classes="mg ml-4 cursor pointer"
                onClick={() => {
                  if (uid) {
                    removeFileFromUid(uid, file.uuid, file.fileId);
                    changeFiles &&
                      changeFiles(
                        files.filter((item: any) => item.fileId !== file.fileId)
                      );
                  }
                }}
              >
                <Icons variant="line" label="closeLarge" size={12} />
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  };
  return (
    <Box direction={"column"} classes="newWorkgroup">
      {modalType === "CREATE_NEW_WORKGROUP" && (
        <Box direction="column" classes="mg mb-20">
          <Typography type="subtitle2">{t("common.workgroupName")}</Typography>
          <InputField
            placeholder={t("workgroup.newWorkgroupPlaceholder")}
            classes="mg mt-6"
            size={"md"}
            maxLength={120}
            value={workgroupInfo?.workgroupName}
            onChange={(e) =>
              handleChangeWorkgroupInfo("workgroupName", e.target.value)
            }
          />
        </Box>
      )}
      <Typography type="subtitle2">{t("common.searchUser")}</Typography>
      <Box classes="mg mt-6">
        <UserSearchField
          width={
            i18n.language === "ko" ? "calc(100% - 131px)" : "calc(100% - 139px)"
          }
          clientSize={clientSize}
          invisibleUserIds={[
            ...addedUserList.map((item) => item.userId),
            ...disableValues,
          ]}
          onClick={(user: Member) =>
            changeAddedUserList([...addedUserList, user])
          }
        />
        <Button
          btnStyle="fill"
          type="secondary"
          label={t("common.chooseMember")}
          classes="mg ml-8"
          size={"lg"}
          onClick={() => changeExtraOpenModalInfo("SELECT_USER")}
          leadingIcon={<Icons variant="line" label="addPerson" />}
        />
      </Box>

      <Box classes="mg mt-20 mb-4" alignItems="center">
        <Typography type="subtitle2">
          {addedUserList?.length === 0 ? (
            <Typography type="subtitle2">
              {modalType === "SELECT_RECEIVER"
                ? t("sendingFile.recipients")
                : t("common.member")}
            </Typography>
          ) : checkboxGroup.selected.length === 0 &&
            modalType !== "SELECT_RECEIVER" ? (
            <Trans
              i18nKey={"common.memberWithCount"}
              values={{ count: addedUserList?.length ?? 0 }}
              components={{
                1: (
                  <Typography
                    type="subtitle2"
                    exactColor={getColor("COLOR_TEXT_BRAND")}
                  />
                ),
              }}
            />
          ) : (
            <Trans
              i18nKey="common.chosenMember"
              values={{
                count:
                  modalType === "SELECT_RECEIVER"
                    ? checkboxGroup.selected?.length
                    : addedUserList?.length,
              }}
              components={{
                1: (
                  <Typography
                    type="subtitle2"
                    exactColor={getColor("COLOR_TEXT_BRAND")}
                  />
                ),
              }}
            />
          )}
        </Typography>
        {modalType === "SELECT_RECEIVER" && (
          <Box alignItems="center" classes="mg ml-auto">
            <Dropdown
              value={t("common.setAuth")}
              btnStyle="link"
              btnSize="lg"
              iconPos="left"
              placeholder={" "}
              buttonClick={buttonClick}
              classes="mg ml-auto"
              leadingIcon={<Icons variant="line" label="shield" isOn />}
              buttonWidth={i18n.language === "ko" ? 154 : 200}
              align={"right"}
              disabled={checkboxGroup.selected.length === 0}
            >
              {[
                { key: "NORMAL", label: t("sendingFile.basic") },
                { key: "CUSTOM", label: t("sendingFile.custom") },
              ].map((item) => (
                <DropdownItem
                  label={item.label}
                  checkColor={item.key === "NORMAL"}
                  checked={item.key === "NORMAL"}
                  type="check"
                  onClick={() => {
                    changeButtonClick(buttonClick + 1);
                    item.key === "NORMAL"
                      ? changePermissionList && changePermissionList([])
                      : changeTargetPermissionUserList &&
                        changeTargetPermissionUserList(checkboxGroup.selected);
                  }}
                  trailingIcon={
                    item.key === "CUSTOM" ? (
                      <Icons variant="line" label="setting" classes="mg ml-8" />
                    ) : undefined
                  }
                />
              ))}
            </Dropdown>
            <Button
              label={t("common.initAuth")}
              btnStyle="link"
              size="lg"
              onClick={() =>
                changePermissionList &&
                changePermissionList(
                  permissionList?.filter(
                    (item) =>
                      !checkboxGroup.selected?.includes(item.targetValue)
                  ) ?? []
                )
              }
              state={
                checkboxGroup.selected.length === 0 ? "disable" : "default"
              }
              leadingIcon={<Icons variant="line" label="redoLeft" isOn />}
            />
          </Box>
        )}
      </Box>
      <Box classes="newWorkgroup-members" direction="column">
        {modalType === "SELECT_RECEIVER" && addedUserList.length > 0 && (
          <Box width={"100%"}>
            <Box classes="newWorkgroup-members--header">
              <Checkbox
                indeterminate={
                  checkboxGroup.selected.length > 0 &&
                  checkboxGroup.selected.length < addedUserList.length
                }
                check={checkboxGroup.selected.length === addedUserList.length}
                onClick={() =>
                  checkboxGroup.handleAllClick(
                    checkboxGroup.selected.length === addedUserList.length
                      ? []
                      : addedUserList.map((item) => item.userId)
                  )
                }
              />
              <Typography
                type="body3_rg"
                classes="mg ml-8"
                width={358}
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("common.idAndName")}
              </Typography>
              <Typography
                type="body3_rg"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("common.permission")}
              </Typography>
            </Box>
          </Box>
        )}
        {addedUserList.length === 0 ? (
          <Box
            direction="column"
            alignItems="center"
            justifyContent="center"
            width={"100%"}
            height={"100%"}
          >
            <img
              src={require("../../shared/images/pages/File/noPeople.png")}
              alt={"thumbnail"}
              width={200}
              height={120}
            />
            <Typography
              type="body4_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
              width={200}
              textAlign="center"
            >
              {modalType === "SELECT_RECEIVER"
                ? t("sendingFile.selectMember")
                : t("workgroup.selectMember")}
            </Typography>
          </Box>
        ) : (
          <Box direction="column" classes="newWorkgroup-members--list">
            {addedUserList.map((item) => {
              const isCustom =
                permissionList &&
                permissionList?.filter(
                  (item1) => item1.targetValue === item.userId
                )?.length > 0;
              return (
                <Box
                  classes={classNames("newWorkgroup-members--user", {
                    selected: checkboxGroup.isSelected(item.userId),
                  })}
                >
                  {modalType === "SELECT_RECEIVER" && (
                    <Checkbox
                      check={checkboxGroup.isSelected(item.userId)}
                      onClick={() => checkboxGroup.handleClick(item.userId)}
                    />
                  )}
                  <NameTag
                    user={
                      item.userType === 1
                        ? {
                            ...item,
                            companyName: loginDto.ecoCommonConfig.companyName,
                          }
                        : item
                    }
                    width={335}
                    showOnlyOne
                    classes="mg ml-8"
                    withoutUserId={false}
                  />
                  {modalType === "SELECT_RECEIVER" && (
                    <PolicyToolTip
                      classes="mg ml-auto"
                      sendingType={isCustom ? "ADVANCED" : "NORMAL"}
                      policy={{
                        authType: "LOGIN",
                        changeCode: "CREATE",
                        createUser: myId,
                        policyCode: null,
                        policyType: "DOCUMENT",
                        syncId: "",
                        targetType: "USER",
                        targetValue: item.userId,
                        uniqueId: null,
                        updateTime: 1,
                        updateUser: myId,
                        workgroupCode: "NONEWORKGROUUP",
                        policy: {
                          ...(permissionList?.filter(
                            (item1) => item1.targetValue === item.userId
                          )[0]?.policy ??
                            utilsCommon.getValueFromKeyinEcoCommonConfig(
                              "ecoCommonConfig"
                            ).workgroupPermissionDTO?.policy),
                        },
                      }}
                      targetViewCount={0}
                      children={
                        <Dropdown
                          placeholder=" "
                          btnSize="lg"
                          buttonWidth={154}
                          align="right"
                          btnStyle="none"
                          buttonClick={buttonClick}
                          positionStrategy="fixed"
                          classes="mg ml-auto"
                          value={
                            isCustom
                              ? t("sendingFile.custom")
                              : t("sendingFile.basic")
                          }
                        >
                          <DropdownItem
                            label={t("sendingFile.basic")}
                            checkColor={!isCustom}
                            checked={!isCustom}
                            type="check"
                            onClick={() => {
                              changePermissionList &&
                                changePermissionList(
                                  permissionList?.filter(
                                    (item1) => item1.targetValue !== item.userId
                                  ) ?? []
                                );
                              changeButtonClick(buttonClick + 1);
                            }}
                          />
                          <DropdownItem
                            label={t("sendingFile.custom")}
                            checkColor={isCustom}
                            checked={isCustom}
                            type="check"
                            trailingIcon={
                              <Icons
                                variant="line"
                                label="setting"
                                classes="mg ml-8"
                              />
                            }
                            onClick={() =>
                              changeTargetPermissionUserList &&
                              changeTargetPermissionUserList([item.userId])
                            }
                          />
                        </Dropdown>
                      }
                      targetAuth={""}
                    />
                  )}
                  <Button
                    btnStyle="icon"
                    classes={
                      modalType === "SELECT_RECEIVER"
                        ? "mg ml-10"
                        : "mg ml-auto"
                    }
                    onClick={() => {
                      changeAddedUserList(
                        addedUserList.filter(
                          (item1) => item1.userId !== item.userId
                        )
                      );
                      checkboxGroup.isSelected(item.userId) &&
                        checkboxGroup.handleClick(item.userId);
                    }}
                    leadingIcon={<Icons variant="line" label="delete" />}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
      {((files && files.length > 0) ||
        (copyFileResp?.files && copyFileResp?.files?.length > 0)) &&
        renderFileList(files ?? copyFileResp?.files)}
      <Box direction="column" classes="mg mt-20">
        <Typography type="subtitle2" classes="mg mb-6">
          {t("common.message")}
        </Typography>
        <InputArea
          placeholder={t("common.messagePlaceholder")}
          size="sm"
          rows={3}
          maxLength={256}
          value={message}
          onChange={(e) => {
            changeMessage && changeMessage(e.target.value);
            handleChangeWorkgroupInfo("invitedMessage", e.target.value);
          }}
        />
      </Box>
    </Box>
  );
};
export default CreateNewWorkgroupPage;
